import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
} from "react";
import {
  Plus,
  StickyNote,
  FileText,
  Shapes,
  Image,
  Group,
  Link2,
  Trash2,
  Copy,
  Undo2,
  Redo2,
  Minus,
  Maximize,
  ArrowUpRight,
  Move,
} from "lucide-react";
import { api, json } from "./api";
import { Modal } from "./components";
import { useResource } from "./useResource";
import { ResourceStatus } from "./ResourceStatus";
import type { EditorHandle } from "./DocumentEditor";
import type {
  Asset,
  Backlink,
  Board,
  BoardContent,
  BoardNode,
  DocumentMeta,
  Entity,
} from "./types";

type Props = {
  initial: Board;
  boards: Board[];
  documents: DocumentMeta[];
  entities: Entity[];
  assets: Asset[];
  readOnly: boolean;
  onSaved: (b: Board) => void;
  onSelect: (b: Board) => void;
  onReload: () => Promise<void>;
  onOpen: (r: Backlink) => void;
  onAsset: (a: Asset) => void;
};
type Gesture = {
  type: "move" | "resize" | "pan";
  pointer: number;
  startX: number;
  startY: number;
  content: BoardContent;
  ids: string[];
};
function labelLines(text: string, width: number) {
  const max = Math.max(8, Math.min(24, Math.floor((width - 22) / 6.2))),
    lines: string[] = [];
  let line = "";
  for (const word of text.split(/\s+/)) {
    if ((line + " " + word).trim().length > max && line) {
      lines.push(line);
      line = word;
    } else line = (line + " " + word).trim();
  }
  if (line) lines.push(line);
  return lines
    .slice(0, 4)
    .map((line, i) =>
      i === 3 && lines.length > 4 ? line.slice(0, -1) + "…" : line,
    );
}
export const BoardEditor = forwardRef<EditorHandle, Props>(
  function BoardEditor(props, ref) {
    const { initial, readOnly, documents, entities, assets } = props,
      resource = useResource(
        initial,
        `/projects/${initial.projectId}/boards/${initial.id}`,
        props.onSaved,
      ),
      { data, change, flush } = resource;
    const [selected, setSelected] = useState<string[]>([]),
      [edge, setEdge] = useState<string | null>(null),
      [pick, setPick] = useState<"document" | "entity" | "image" | null>(null),
      [target, setTarget] = useState(""),
      [connect, setConnect] = useState(false),
      [from, setFrom] = useState(""),
      [to, setTo] = useState(""),
      [edgeLabel, setEdgeLabel] = useState(""),
      [newBoard, setNewBoard] = useState(false),
      [newName, setNewName] = useState(""),
      [trash, setTrash] = useState(false),
      [error, setError] = useState(""),
      [imageError, setImageError] = useState(""),
      [uploading, setUploading] = useState(false),
      [panMode, setPanMode] = useState(false),
      [viewport, setViewport] = useState(initial.content.viewport),
      [historySize, setHistorySize] = useState(0);
    const surface = useRef<HTMLDivElement>(null),
      imageInput = useRef<HTMLInputElement>(null),
      gesture = useRef<Gesture | null>(null),
      past = useRef<BoardContent[]>([]),
      future = useRef<BoardContent[]>([]),
      space = useRef(false),
      current = useRef(data);
    current.current = data;
    useImperativeHandle(ref, () => ({ flush }), [flush]);
    useEffect(() => {
      if (resource.state === "saved") setViewport(data.content.viewport);
    }, [data.content.viewport, resource.state]);
    function remember() {
      past.current = [
        ...past.current.slice(-49),
        structuredClone(current.current.content),
      ];
      future.current = [];
      setHistorySize((v) => v + 1);
    }
    function edit(content: BoardContent, record = true) {
      if (readOnly) return;
      if (record) remember();
      change({ content });
    }
    function undo() {
      if (readOnly || !past.current.length) return;
      future.current.push(structuredClone(data.content));
      const content = past.current.pop()!;
      change({ content });
      setViewport(content.viewport);
      setHistorySize((v) => v + 1);
      setSelected([]);
    }
    function redo() {
      if (readOnly || !future.current.length) return;
      past.current.push(structuredClone(data.content));
      const content = future.current.pop()!;
      change({ content });
      setViewport(content.viewport);
      setHistorySize((v) => v + 1);
      setSelected([]);
    }
    function title(n: BoardNode) {
      return n.type === "document"
        ? documents.find((d) => d.id === n.targetId)?.title ||
            "Документ в корзине"
        : n.type === "entity"
          ? entities.find((e) => e.id === n.targetId)?.title ||
            "Сущность в корзине"
          : n.type === "image"
            ? assets.find((a) => a.id === n.targetId)?.name ||
              "Изображение недоступно"
            : n.type === "group"
              ? n.text || "Группа"
              : "Заметка";
    }
    function open(n: BoardNode) {
      if (n.type === "document" && documents.some((d) => d.id === n.targetId))
        props.onOpen({ type: "document", id: n.targetId!, title: title(n) });
      if (n.type === "entity" && entities.some((e) => e.id === n.targetId))
        props.onOpen({ type: "entity", id: n.targetId!, title: title(n) });
    }
    function add(type: BoardNode["type"], targetId?: string) {
      const area = surface.current?.getBoundingClientRect(),
        offset = (data.content.nodes.length % 5) * 18;
      const node: BoardNode = {
        id: crypto.randomUUID(),
        type,
        ...(targetId ? { targetId } : {}),
        x: Math.round(
          ((area?.width || 600) / 2 - viewport.x) / viewport.zoom -
            120 +
            offset,
        ),
        y: Math.round((120 - viewport.y) / viewport.zoom + offset),
        width: type === "group" ? 620 : 240,
        height: type === "group" ? 400 : type === "image" ? 210 : 165,
        text:
          type === "note"
            ? "Новая заметка"
            : type === "group"
              ? "Новая группа"
              : "",
        color: type === "note" ? "amber" : "neutral",
      };
      edit({ ...data.content, nodes: [...data.content.nodes, node], viewport });
      setSelected([node.id]);
      setEdge(null);
    }
    function remove() {
      if (edge) {
        edit({
          ...data.content,
          edges: data.content.edges.filter((e) => e.id !== edge),
        });
        setEdge(null);
        return;
      }
      if (!selected.length) return;
      edit({
        ...data.content,
        nodes: data.content.nodes.filter((n) => !selected.includes(n.id)),
        edges: data.content.edges.filter(
          (e) => !selected.includes(e.from) && !selected.includes(e.to),
        ),
      });
      setSelected([]);
    }
    function duplicate() {
      if (!selected.length || readOnly) return;
      const mapping = new Map(selected.map((id) => [id, crypto.randomUUID()]));
      const copies = data.content.nodes
        .filter((n) => selected.includes(n.id))
        .map((n) => ({
          ...n,
          id: mapping.get(n.id)!,
          x: n.x + 30,
          y: n.y + 30,
        }));
      const edges = data.content.edges
        .filter((e) => mapping.has(e.from) && mapping.has(e.to))
        .map((e) => ({
          ...e,
          id: crypto.randomUUID(),
          from: mapping.get(e.from)!,
          to: mapping.get(e.to)!,
        }));
      edit({
        ...data.content,
        nodes: [...data.content.nodes, ...copies],
        edges: [...data.content.edges, ...edges],
      });
      setSelected(copies.map((n) => n.id));
    }
    function setView(next: BoardContent["viewport"]) {
      setViewport(next);
      if (!readOnly)
        change({
          content: { ...resource.getCurrent().content, viewport: next },
        });
    }
    function zoom(delta: number, cx?: number, cy?: number) {
      const z = Math.max(
        0.2,
        Math.min(2, Math.round((viewport.zoom + delta) * 100) / 100),
      );
      const x = cx ?? (surface.current?.clientWidth || 600) / 2,
        y = cy ?? (surface.current?.clientHeight || 500) / 2;
      setView({
        zoom: z,
        x: x - ((x - viewport.x) * z) / viewport.zoom,
        y: y - ((y - viewport.y) * z) / viewport.zoom,
      });
    }
    function fit() {
      const ns = data.content.nodes;
      if (!ns.length) {
        setView({ x: 50, y: 50, zoom: 1 });
        return;
      }
      const left = Math.min(...ns.map((n) => n.x)),
        top = Math.min(...ns.map((n) => n.y)),
        right = Math.max(...ns.map((n) => n.x + n.width)),
        bottom = Math.max(...ns.map((n) => n.y + n.height)),
        w = surface.current?.clientWidth || 800,
        h = surface.current?.clientHeight || 500,
        z = Math.min(
          1.3,
          Math.max(
            0.2,
            Math.min((w - 80) / (right - left), (h - 80) / (bottom - top)),
          ),
        );
      setView({
        zoom: z,
        x: (w - (right - left) * z) / 2 - left * z,
        y: (h - (bottom - top) * z) / 2 - top * z,
      });
    }
    function down(e: ReactPointerEvent<HTMLDivElement>) {
      if (e.button !== 0 && e.button !== 1) return;
      const el = e.target as HTMLElement;
      if (el.closest("button,input,textarea,select,a,.board-edge")) return;
      const card = el.closest<HTMLElement>("[data-node]"),
        resize = el.closest("[data-resize]"),
        panning = space.current || panMode || e.button === 1 || !card;
      let ids = selected;
      if (card && !panning) {
        const id = card.dataset.node!;
        ids = e.shiftKey
          ? selected.includes(id)
            ? selected.filter((v) => v !== id)
            : [...selected, id]
          : selected.includes(id)
            ? selected
            : [id];
        setSelected(ids);
        setEdge(null);
      } else if (!card && !space.current && !panMode) {
        setSelected([]);
        setEdge(null);
      }
      if (readOnly && !panning) return;
      if (!panning) remember();
      if (!panning && !resize) {
        const all = new Set(ids);
        for (const id of ids) {
          const group = data.content.nodes.find(
            (n) => n.id === id && n.type === "group",
          );
          if (group)
            for (const n of data.content.nodes)
              if (
                n.x >= group.x &&
                n.y >= group.y &&
                n.x + n.width <= group.x + group.width &&
                n.y + n.height <= group.y + group.height
              )
                all.add(n.id);
        }
        ids = [...all];
      }
      gesture.current = {
        type: panning ? "pan" : resize ? "resize" : "move",
        pointer: e.pointerId,
        startX: e.clientX,
        startY: e.clientY,
        content: structuredClone({ ...data.content, viewport }),
        ids,
      };
      surface.current?.setPointerCapture(e.pointerId);
      surface.current?.focus();
      e.preventDefault();
    }
    function move(e: ReactPointerEvent<HTMLDivElement>) {
      const g = gesture.current;
      if (!g || g.pointer !== e.pointerId) return;
      const dx = e.clientX - g.startX,
        dy = e.clientY - g.startY;
      if (g.type === "pan") {
        setView({
          ...g.content.viewport,
          x: g.content.viewport.x + dx,
          y: g.content.viewport.y + dy,
        });
        return;
      }
      edit(
        {
          ...g.content,
          nodes: g.content.nodes.map((n) =>
            g.ids.includes(n.id)
              ? g.type === "move"
                ? {
                    ...n,
                    x: Math.max(
                      -100000,
                      Math.min(100000, Math.round(n.x + dx / viewport.zoom)),
                    ),
                    y: Math.max(
                      -100000,
                      Math.min(100000, Math.round(n.y + dy / viewport.zoom)),
                    ),
                  }
                : {
                    ...n,
                    width: Math.max(
                      120,
                      Math.min(4000, Math.round(n.width + dx / viewport.zoom)),
                    ),
                    height: Math.max(
                      80,
                      Math.min(4000, Math.round(n.height + dy / viewport.zoom)),
                    ),
                  }
              : n,
          ),
        },
        false,
      );
    }
    const node =
        selected.length === 1
          ? data.content.nodes.find((n) => n.id === selected[0])
          : null,
      chosenEdge = data.content.edges.find((e) => e.id === edge);
    function patchNode(patch: Partial<BoardNode>) {
      if (!node) return;
      edit({
        ...data.content,
        nodes: data.content.nodes.map((n) =>
          n.id === node.id ? { ...n, ...patch } : n,
        ),
      });
    }
    useEffect(() => {
      const el = surface.current;
      if (!el) return;
      const wheel = (e: WheelEvent) => {
        e.preventDefault();
        if (e.ctrlKey || e.metaKey) {
          const rect = el.getBoundingClientRect();
          zoom(
            e.deltaY < 0 ? 0.1 : -0.1,
            e.clientX - rect.left,
            e.clientY - rect.top,
          );
        } else
          setView({
            ...viewport,
            x: viewport.x - e.deltaX,
            y: viewport.y - e.deltaY,
          });
      };
      el.addEventListener("wheel", wheel, { passive: false });
      return () => el.removeEventListener("wheel", wheel);
    }, [viewport, readOnly]);
    const targets =
      pick === "document"
        ? documents.map((d) => ({ id: d.id, title: d.title }))
        : pick === "entity"
          ? entities.map((e) => ({ id: e.id, title: e.title }))
          : assets.map((a) => ({ id: a.id, title: a.name }));
    return (
      <div className="board-page">
        <div className="board-top">
          <select
            aria-label="Выбор доски"
            value={data.id}
            onChange={(e) => {
              const b = props.boards.find((b) => b.id === e.target.value);
              if (b) props.onSelect(b);
            }}
          >
            {props.boards.map((b) => (
              <option key={b.id} value={b.id}>
                {b.id === data.id ? data.title : b.title}
              </option>
            ))}
          </select>
          <button
            className="icon-button"
            disabled={readOnly}
            aria-label="Создать доску"
            onClick={() => {
              setNewName("");
              setNewBoard(true);
            }}
          >
            <Plus size={16} />
          </button>
          <ResourceStatus resource={resource} readOnly={readOnly} />
          <button
            className="icon-button"
            aria-label="Удалить доску"
            disabled={readOnly}
            onClick={() => setTrash(true)}
          >
            <Trash2 size={15} />
          </button>
        </div>
        {error && (
          <div className="error-message" role="alert">
            {error}
          </div>
        )}
        <div className="board-toolbar">
          <button disabled={readOnly} onClick={() => add("note")}>
            <StickyNote size={16} />
            Заметка
          </button>
          {(
            [
              ["document", "Документ", FileText],
              ["entity", "Сущность", Shapes],
              ["image", "Картинка", Image],
            ] as const
          ).map(([type, label, Icon]) => (
            <button
              key={type}
              disabled={readOnly}
              onClick={() => {
                setTarget("");
                setPick(type);
              }}
            >
              <Icon size={16} />
              {label}
            </button>
          ))}
          <button disabled={readOnly} onClick={() => add("group")}>
            <Group size={16} />
            Группа
          </button>
          <button
            disabled={readOnly || data.content.nodes.length < 2}
            onClick={() => {
              setFrom(selected[0] || data.content.nodes[0]?.id || "");
              setTo("");
              setEdgeLabel("");
              setConnect(true);
            }}
          >
            <Link2 size={16} />
            Связь
          </button>
          <span className="toolbar-divider" />
          <button
            className="icon-button"
            aria-label="Отменить на доске"
            disabled={readOnly || !past.current.length}
            onClick={undo}
          >
            <Undo2 size={16} />
          </button>
          <button
            className="icon-button"
            aria-label="Повторить на доске"
            disabled={readOnly || !future.current.length}
            onClick={redo}
          >
            <Redo2 size={16} />
          </button>
        </div>
        <div className="board-layout">
          <div className="board-canvas-area">
            <div
              className={`board-canvas ${panMode ? "pan-mode" : ""}`}
              ref={surface}
              tabIndex={0}
              role="region"
              aria-label="Холст доски"
              style={{
                backgroundSize: `${20 * viewport.zoom}px ${20 * viewport.zoom}px`,
                backgroundPosition: `${viewport.x}px ${viewport.y}px`,
              }}
              onPointerDown={down}
              onPointerMove={move}
              onPointerUp={() => {
                gesture.current = null;
              }}
              onPointerCancel={() => {
                gesture.current = null;
              }}
              onKeyDown={(e) => {
                if ((e.target as HTMLElement).closest("input,textarea,select"))
                  return;
                if (e.code === "Space") {
                  space.current = true;
                  e.preventDefault();
                }
                if (readOnly) return;
                if (e.key === "Delete" || e.key === "Backspace") {
                  e.preventDefault();
                  remove();
                }
                if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "d") {
                  e.preventDefault();
                  duplicate();
                }
                if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "z") {
                  e.preventDefault();
                  e.shiftKey ? redo() : undo();
                }
              }}
              onKeyUp={(e) => {
                if (e.code === "Space") space.current = false;
              }}
              onBlur={() => {
                space.current = false;
              }}
            >
              <div
                className="board-world"
                style={{
                  transform: `translate(${viewport.x}px,${viewport.y}px) scale(${viewport.zoom})`,
                }}
              >
                <svg className="board-links" aria-label="Связи карточек">
                  <defs>
                    <marker
                      id={`arrow-${data.id}`}
                      viewBox="0 0 10 10"
                      refX="9"
                      refY="5"
                      markerWidth="7"
                      markerHeight="7"
                      orient="auto-start-reverse"
                    >
                      <path d="M 0 0 L 10 5 L 0 10 z" fill="currentColor" />
                    </marker>
                  </defs>
                  {data.content.edges.map((e) => {
                    const a = data.content.nodes.find((n) => n.id === e.from),
                      b = data.content.nodes.find((n) => n.id === e.to);
                    if (!a || !b) return null;
                    const x = a.x + a.width,
                      y = a.y + a.height / 2,
                      tx = b.x,
                      ty = b.y + b.height / 2;
                    const d = `M ${x} ${y} C ${x + 80} ${y},${tx - 80} ${ty},${tx} ${ty}`;
                    const lines = labelLines(e.label, Math.abs(tx - x));
                    return (
                      <g
                        key={e.id}
                        className={`board-edge ${edge === e.id ? "selected" : ""}`}
                        onClick={() => {
                          setEdge(e.id);
                          setSelected([]);
                        }}
                      >
                        <path d={d} markerEnd={`url(#arrow-${data.id})`} />
                        <path d={d} className="edge-hit" />
                        {e.label && (
                          <text
                            x={(x + tx) / 2}
                            y={(y + ty) / 2 - lines.length * 13 - 4}
                            textAnchor="middle"
                          >
                            <title>{e.label}</title>
                            {lines.map((line, i) => (
                              <tspan key={i} x={(x + tx) / 2} dy={i ? 14 : 0}>
                                {line}
                              </tspan>
                            ))}
                          </text>
                        )}
                      </g>
                    );
                  })}
                </svg>
                {[...data.content.nodes]
                  .sort(
                    (a, b) =>
                      Number(b.type === "group") - Number(a.type === "group"),
                  )
                  .map((n) => (
                    <div
                      data-node={n.id}
                      key={n.id}
                      className={`board-node node-${n.type} color-${n.color} ${selected.includes(n.id) ? "selected" : ""}`}
                      style={{
                        left: n.x,
                        top: n.y,
                        width: n.width,
                        height: n.height,
                      }}
                      onDoubleClick={() => open(n)}
                    >
                      <div className="board-node-heading">
                        <span>
                          {n.type === "note"
                            ? "ЗАМЕТКА"
                            : n.type === "group"
                              ? "ГРУППА"
                              : n.type === "document"
                                ? "ДОКУМЕНТ"
                                : n.type === "entity"
                                  ? "СУЩНОСТЬ"
                                  : "ИЗОБРАЖЕНИЕ"}
                        </span>
                        {["document", "entity"].includes(n.type) && (
                          <button
                            className="icon-button"
                            aria-label={`Открыть ${title(n)}`}
                            onClick={() => open(n)}
                          >
                            <ArrowUpRight size={14} />
                          </button>
                        )}
                      </div>
                      {n.type === "note" ? (
                        <p>{n.text}</p>
                      ) : n.type === "image" ? (
                        <>
                          <img
                            draggable={false}
                            src={`/api/projects/${initial.projectId}/assets/${n.targetId}`}
                            alt={title(n)}
                          />
                          <span>{title(n)}</span>
                        </>
                      ) : (
                        <>
                          <h3>{title(n)}</h3>
                          {n.type === "document" && (
                            <p>
                              {
                                documents.find((d) => d.id === n.targetId)
                                  ?.excerpt
                              }
                            </p>
                          )}
                          {n.type === "entity" && (
                            <div className="board-entity-params">
                              {entities
                                .find((e) => e.id === n.targetId)
                                ?.properties.slice(0, 4)
                                .map((p) => (
                                  <span key={p.id}>
                                    {p.name}:{" "}
                                    {typeof p.value === "object"
                                      ? "↗"
                                      : p.type === "boolean"
                                        ? p.value
                                          ? "Да"
                                          : "Нет"
                                        : String(p.value)}
                                  </span>
                                ))}
                            </div>
                          )}
                        </>
                      )}
                      {!readOnly && selected.includes(n.id) && (
                        <span className="board-resize" data-resize="true" />
                      )}
                    </div>
                  ))}
              </div>
              {!data.content.nodes.length && (
                <div className="board-empty">
                  <StickyNote size={35} strokeWidth={1} />
                  <h2>Разложи идею по полочкам</h2>
                  <p>
                    Добавь заметки, документы и сущности.
                    <br />
                    Соедини их, чтобы увидеть картину целиком.
                  </p>
                </div>
              )}
            </div>
            <div className="board-bottom">
              <span>Shift: несколько карточек · Space + движение: холст</span>
              <div>
                <button
                  aria-label="Перемещение холста"
                  aria-pressed={panMode}
                  className="icon-button"
                  onClick={() => setPanMode(!panMode)}
                >
                  <Move size={15} />
                </button>
                <button
                  className="icon-button"
                  aria-label="Уменьшить масштаб"
                  onClick={() => zoom(-0.1)}
                >
                  <Minus size={15} />
                </button>
                <span>{Math.round(viewport.zoom * 100)}%</span>
                <button
                  className="icon-button"
                  aria-label="Увеличить масштаб"
                  onClick={() => zoom(0.1)}
                >
                  <Plus size={15} />
                </button>
                <button
                  className="icon-button"
                  aria-label="Показать всю доску"
                  onClick={fit}
                >
                  <Maximize size={15} />
                </button>
              </div>
            </div>
          </div>
          <aside className="board-inspector">
            <label>
              Название доски
              <input
                aria-label="Название доски"
                value={data.title}
                disabled={readOnly}
                maxLength={160}
                onChange={(e) => change({ title: e.target.value })}
              />
            </label>
            {node ? (
              <>
                <div className="inspector-group">
                  <span className="eyebrow">ВЫБРАННАЯ КАРТОЧКА</span>
                  <h3>{title(node)}</h3>
                </div>
                {(node.type === "note" || node.type === "group") && (
                  <label>
                    {node.type === "group"
                      ? "Название группы"
                      : "Текст заметки"}
                    <textarea
                      aria-label={
                        node.type === "group"
                          ? "Название группы"
                          : "Текст заметки"
                      }
                      rows={5}
                      disabled={readOnly}
                      maxLength={8000}
                      value={node.text}
                      onChange={(e) => patchNode({ text: e.target.value })}
                    />
                  </label>
                )}
                <div className="board-properties">
                  {(
                    [
                      ["x", "X"],
                      ["y", "Y"],
                      ["width", "Ширина"],
                      ["height", "Высота"],
                    ] as const
                  ).map(([key, label]) => (
                    <label key={key}>
                      {label}
                      <input
                        type="number"
                        aria-label={label}
                        value={node[key]}
                        disabled={readOnly}
                        onChange={(e) => {
                          const v = Number(e.target.value);
                          if (Number.isFinite(v))
                            patchNode({
                              [key]:
                                key === "width" || key === "height"
                                  ? Math.min(
                                      4000,
                                      Math.max(key === "width" ? 120 : 80, v),
                                    )
                                  : Math.max(-100000, Math.min(100000, v)),
                            });
                        }}
                      />
                    </label>
                  ))}
                </div>
                <label>
                  Цвет карточки
                  <select
                    value={node.color}
                    disabled={readOnly}
                    onChange={(e) =>
                      patchNode({ color: e.target.value as BoardNode["color"] })
                    }
                  >
                    <option value="neutral">Нейтральный</option>
                    <option value="violet">Фиолетовый</option>
                    <option value="amber">Тёплый</option>
                    <option value="green">Зелёный</option>
                  </select>
                </label>
                <div className="board-selection-actions">
                  <button
                    className="secondary"
                    disabled={readOnly}
                    onClick={duplicate}
                  >
                    <Copy size={14} />
                    Копия
                  </button>
                  <button
                    className="icon-button"
                    disabled={readOnly}
                    aria-label="Удалить карточку"
                    onClick={remove}
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
                {["document", "entity"].includes(node.type) && (
                  <button className="text-button" onClick={() => open(node)}>
                    Открыть материал <ArrowUpRight size={14} />
                  </button>
                )}
              </>
            ) : chosenEdge ? (
              <>
                <label>
                  Подпись связи
                  <input
                    aria-label="Подпись связи"
                    maxLength={120}
                    value={chosenEdge.label}
                    disabled={readOnly}
                    onChange={(e) =>
                      edit({
                        ...data.content,
                        edges: data.content.edges.map((x) =>
                          x.id === edge ? { ...x, label: e.target.value } : x,
                        ),
                      })
                    }
                  />
                </label>
                <button
                  className="secondary"
                  disabled={readOnly}
                  onClick={remove}
                >
                  <Trash2 size={14} />
                  Удалить связь
                </button>
              </>
            ) : selected.length > 1 ? (
              <>
                <p>{selected.length} карточек выбрано</p>
                <button
                  className="secondary"
                  disabled={readOnly}
                  onClick={duplicate}
                >
                  Дублировать выбранное
                </button>
                <button
                  className="text-button"
                  disabled={readOnly}
                  onClick={remove}
                >
                  Удалить выбранное
                </button>
              </>
            ) : (
              <p className="muted small-text board-inspector-hint">
                Выбери карточку, чтобы изменить её размер, текст и цвет. Двойной
                щелчок открывает связанный материал.
              </p>
            )}
          </aside>
        </div>
        {pick && (
          <Modal title="Добавить материал на доску" close={() => setPick(null)}>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                if (target) {
                  add(pick, target);
                  setPick(null);
                }
              }}
            >
              <label>
                Материал
                <select
                  autoFocus
                  required
                  value={target}
                  onChange={(e) => setTarget(e.target.value)}
                >
                  <option value="">Выбери материал</option>
                  {targets.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.title}
                    </option>
                  ))}
                </select>
              </label>
              {!targets.length && (
                <p className="small-text muted">
                  {pick === "image"
                    ? "Загрузи изображение с компьютера или добавь его через документ."
                    : "Сначала создай материал в соответствующем разделе проекта."}
                </p>
              )}
              {pick === "image" && (
                <>
                  <input
                    ref={imageInput}
                    type="file"
                    className="file-input"
                    accept="image/png,image/jpeg,image/webp"
                    aria-label="Загрузить картинку на доску"
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      setImageError("");
                      setUploading(true);
                      try {
                        if (file.size > 20 * 1024 * 1024)
                          throw new Error("Изображение превышает 20 МБ.");
                        const body = new FormData();
                        body.append("image", file);
                        const asset = await api<Asset>(
                          `/projects/${initial.projectId}/assets`,
                          { method: "POST", body },
                        );
                        props.onAsset(asset);
                        add("image", asset.id);
                        setPick(null);
                      } catch (err) {
                        setImageError((err as Error).message);
                      } finally {
                        setUploading(false);
                      }
                    }}
                  />
                  <button
                    className="secondary"
                    type="button"
                    disabled={uploading}
                    onClick={() => imageInput.current?.click()}
                  >
                    <Image size={15} />
                    {uploading ? "Загрузка…" : "Загрузить с компьютера"}
                  </button>
                  {imageError && (
                    <div className="error-message" role="alert">
                      {imageError}
                    </div>
                  )}
                </>
              )}
              <div className="modal-actions">
                <button
                  type="button"
                  className="secondary"
                  onClick={() => setPick(null)}
                >
                  Отмена
                </button>
                <button className="primary" disabled={!target}>
                  Добавить карточку
                </button>
              </div>
            </form>
          </Modal>
        )}
        {connect && (
          <Modal title="Соединить карточки" close={() => setConnect(false)}>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                if (from && to && from !== to) {
                  edit({
                    ...data.content,
                    edges: [
                      ...data.content.edges,
                      { id: crypto.randomUUID(), from, to, label: edgeLabel },
                    ],
                  });
                  setConnect(false);
                }
              }}
            >
              <label>
                Откуда
                <select
                  aria-label="Откуда"
                  value={from}
                  onChange={(e) => setFrom(e.target.value)}
                >
                  {data.content.nodes.map((n) => (
                    <option key={n.id} value={n.id}>
                      {title(n)}
                      {n.type === "note" ? `: ${n.text.slice(0, 25)}` : ""}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Куда
                <select
                  aria-label="Куда"
                  required
                  value={to}
                  onChange={(e) => setTo(e.target.value)}
                >
                  <option value="">Выбери карточку</option>
                  {data.content.nodes
                    .filter((n) => n.id !== from)
                    .map((n) => (
                      <option key={n.id} value={n.id}>
                        {title(n)}
                        {n.type === "note" ? `: ${n.text.slice(0, 25)}` : ""}
                      </option>
                    ))}
                </select>
              </label>
              <label>
                Подпись
                <input
                  value={edgeLabel}
                  maxLength={120}
                  onChange={(e) => setEdgeLabel(e.target.value)}
                />
              </label>
              <div className="modal-actions">
                <button
                  type="button"
                  className="secondary"
                  onClick={() => setConnect(false)}
                >
                  Отмена
                </button>
                <button className="primary" disabled={!to || from === to}>
                  Создать связь
                </button>
              </div>
            </form>
          </Modal>
        )}
        {newBoard && (
          <Modal title="Новая доска" close={() => setNewBoard(false)}>
            <form
              onSubmit={async (e) => {
                e.preventDefault();
                try {
                  if (!(await flush())) return;
                  const b = await api<Board>(
                    `/projects/${initial.projectId}/boards`,
                    json("POST", {
                      title: newName,
                      content: {
                        nodes: [],
                        edges: [],
                        viewport: { x: 50, y: 50, zoom: 1 },
                      },
                    }),
                  );
                  await props.onReload();
                  setNewBoard(false);
                  props.onSelect(b);
                } catch (err) {
                  setError((err as Error).message);
                }
              }}
            >
              <label>
                Название
                <input
                  required
                  autoFocus
                  maxLength={160}
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                />
              </label>
              <div className="modal-actions">
                <button
                  type="button"
                  className="secondary"
                  onClick={() => setNewBoard(false)}
                >
                  Отмена
                </button>
                <button className="primary">Создать доску</button>
              </div>
            </form>
          </Modal>
        )}
        {trash && (
          <Modal
            title="Переместить доску в корзину?"
            close={() => setTrash(false)}
          >
            <p>
              Доска и все её карточки сохранятся в корзине. Связанные материалы
              останутся в проекте.
            </p>
            <div className="modal-actions">
              <button className="secondary" onClick={() => setTrash(false)}>
                Отмена
              </button>
              <button
                className="danger"
                onClick={async () => {
                  try {
                    if (!(await flush())) return;
                    await api(
                      `/projects/${initial.projectId}/boards/${data.id}/trash`,
                      json("POST", { version: resource.getCurrent().version }),
                    );
                    setTrash(false);
                    await props.onReload();
                  } catch (e) {
                    setError((e as Error).message);
                  }
                }}
              >
                В корзину
              </button>
            </div>
          </Modal>
        )}
      </div>
    );
  },
);
